/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api";
import {
  Activity, Users, MousePointerClick, Target, Globe, TrendingUp,
  Loader2, ShieldCheck, Trash2, ChevronRight,
} from "lucide-react";

const RANGE_OPTIONS = [
  { value: 1, label: "Aujourd'hui" },
  { value: 7, label: "7 jours" },
  { value: 30, label: "30 jours" },
  { value: 90, label: "90 jours" },
];

const CATEGORY_LABEL = {
  direct: "Accès direct",
  social: "Réseaux sociaux",
  search: "Moteurs de recherche",
  referral: "Sites référents",
  internal: "Navigation interne",
};

const Stat = ({ icon: Icon, label, value, tone = "slate" }) => (
  <div className={`rounded-xl border bg-white p-4 border-slate-200`}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
      <Icon className={`w-3.5 h-3.5 text-${tone}-600`} />
      {label}
    </div>
    <div className="mt-1.5 text-2xl font-serif text-slate-900">{value ?? "—"}</div>
  </div>
);

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const setStateAsync = (fn) => { if (!cancelled) queueMicrotask(fn); };
    setStateAsync(() => { setLoading(true); setError(null); });
    Promise.all([
      apiClient.get(`/analytics/dashboard?days=${days}`),
      apiClient.get(`/analytics/funnel?days=${days}`),
    ]).then(([d, f]) => {
      setStateAsync(() => { setData(d.data); setFunnel(f.data); });
    }).catch((e) => {
      setStateAsync(() => setError(e?.response?.status === 403 ? "Accès réservé aux administrateurs." : "Chargement impossible."));
    }).finally(() => {
      setStateAsync(() => setLoading(false));
    });
    return () => { cancelled = true; };
  }, [days]);

  const purgeOld = async () => {
    if (!window.confirm("Purger tous les événements bruts au-delà de 90 jours ?")) return;
    try {
      const r = await apiClient.delete("/analytics/purge-old");
      alert(`${r.data.deleted} événements supprimés (rétention : ${r.data.retention_days} jours)`);
    } catch { alert("Erreur lors de la purge."); }
  };

  const maxVisits = data?.timeseries?.reduce((m, p) => Math.max(m, p.views), 0) || 1;

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <Navbar variant="light" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {/* Hero */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C9A961]">
              <Activity className="w-3.5 h-3.5" /> Tableau de bord analytics
            </div>
            <h1 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B3A2A]">
              Audience CONFILEX
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Mesure interne, RGPD-by-design : aucun outil tiers, IP hashée avec sel rotatif quotidien,
              consentement explicite des visiteurs.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.value}
                onClick={() => setDays(r.value)}
                data-testid={`range-${r.value}d-btn`}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  days === r.value
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
              >
                {r.label}
              </button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={purgeOld}
              data-testid="purge-old-btn"
              className="ml-2 text-xs gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Purger anciens
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-12">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm p-4">{error}</div>
        )}

        {data && !loading && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8" data-testid="analytics-kpis">
              <Stat icon={Activity} label="Visites" value={data.totals.visits.toLocaleString("fr-FR")} tone="emerald" />
              <Stat icon={Users} label="Visiteurs uniques" value={data.totals.unique_visitors.toLocaleString("fr-FR")} tone="indigo" />
              <Stat icon={ShieldCheck} label="Identifiés (connectés)" value={data.totals.identified_visitors.toLocaleString("fr-FR")} tone="amber" />
              <Stat icon={MousePointerClick} label="Clics suivis" value={data.totals.clicks_tracked.toLocaleString("fr-FR")} tone="rose" />
              <Stat icon={Target} label="Conversions" value={data.totals.conversions.toLocaleString("fr-FR")} tone="violet" />
            </div>

            {/* Time series */}
            <Card className="p-6 mb-6 border-slate-200 bg-white" data-testid="timeseries-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-slate-400" /> Visites quotidiennes
                </h2>
                <span className="text-xs text-slate-400">Max {maxVisits}</span>
              </div>
              <div className="flex items-end gap-1 h-32">
                {data.timeseries.map((p) => {
                  const h = Math.max(2, (p.views / maxVisits) * 100);
                  return (
                    <div
                      key={p.date}
                      className="flex-1 flex flex-col items-center justify-end group relative"
                      title={`${p.date} — ${p.views} visites (${p.unique} uniques)`}
                    >
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-amber-300 to-amber-500 hover:from-amber-400 hover:to-amber-600 transition-colors"
                        style={{ height: `${h}%` }}
                      />
                      <div className="absolute -top-7 opacity-0 group-hover:opacity-100 transition bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none">
                        {p.views} ({p.unique})
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex justify-between text-[10px] text-slate-400">
                <span>{data.timeseries[0]?.date}</span>
                <span>{data.timeseries.at(-1)?.date}</span>
              </div>
            </Card>

            {/* Funnel + Sources */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {funnel && (
                <Card className="p-6 border-slate-200 bg-white" data-testid="funnel-card">
                  <h2 className="text-sm font-medium text-slate-700 mb-4">Funnel de conversion</h2>
                  <div className="space-y-2.5">
                    {funnel.steps.map((s, i) => (
                      <div key={s.step} data-testid={`funnel-step-${i}`}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-700">{s.step}</span>
                          <span className="text-slate-500 font-medium">{s.count.toLocaleString("fr-FR")} · {s.pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-indigo-400 to-amber-500"
                            style={{ width: `${Math.min(100, s.pct)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <Card className="p-6 border-slate-200 bg-white" data-testid="sources-card">
                <h2 className="text-sm font-medium text-slate-700 mb-4 flex items-center gap-1.5">
                  <Globe className="w-4 h-4 text-slate-400" /> Sources de trafic
                </h2>
                {data.top_sources.length === 0 ? (
                  <p className="text-xs text-slate-500">Aucune donnée pour cette période.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data.top_sources.map((s, i) => (
                      <li key={i} className="py-2 flex items-center justify-between text-sm">
                        <div className="min-w-0">
                          <div className="text-slate-900 truncate">{s.source}</div>
                          <Badge variant="outline" className="mt-0.5 text-[10px] capitalize">
                            {CATEGORY_LABEL[s.category] || s.category}
                          </Badge>
                        </div>
                        <span className="text-slate-500 text-xs font-medium tabular-nums">{s.visits}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            {/* Top pages + Clicks */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card className="p-6 border-slate-200 bg-white" data-testid="top-pages-card">
                <h2 className="text-sm font-medium text-slate-700 mb-4">Pages les plus consultées</h2>
                {data.top_pages.length === 0 ? (
                  <p className="text-xs text-slate-500">Aucune donnée.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data.top_pages.map((p, i) => (
                      <li key={p.path} className="py-1.5 flex items-center justify-between text-sm gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10px] text-slate-400 tabular-nums w-5 shrink-0">{i + 1}.</span>
                          <span className="text-slate-700 truncate font-mono text-xs">{p.path}</span>
                        </div>
                        <span className="text-slate-500 text-xs font-medium tabular-nums">{p.views}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card className="p-6 border-slate-200 bg-white" data-testid="top-clicks-card">
                <h2 className="text-sm font-medium text-slate-700 mb-4">Clics importants</h2>
                {data.top_clicks.length === 0 ? (
                  <p className="text-xs text-slate-500">Aucun clic suivi sur cette période.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data.top_clicks.map((c, i) => (
                      <li key={i} className="py-2 flex items-center justify-between text-sm gap-3">
                        <div className="min-w-0">
                          <div className="text-slate-900 truncate text-xs font-medium">{c.label || c.target}</div>
                          {c.label && <div className="text-[10px] text-slate-400 truncate">{c.target}</div>}
                        </div>
                        <span className="text-slate-500 text-xs font-medium tabular-nums">{c.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            {/* Conversions */}
            {Object.keys(data.conversions_by_type).length > 0 && (
              <Card className="p-6 border-slate-200 bg-white" data-testid="conversions-card">
                <h2 className="text-sm font-medium text-slate-700 mb-4">Conversions par type</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {Object.entries(data.conversions_by_type).map(([k, v]) => (
                    <div key={k} className="rounded-md border border-slate-200 px-3 py-2 text-xs">
                      <div className="text-slate-500">{k}</div>
                      <div className="text-base font-medium text-slate-900 mt-0.5">{v}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <div className="mt-8 text-[11px] text-slate-400 flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" />
              Données agrégées en interne — aucun outil tiers, aucun cookie marketing, IP hashées avec sel rotatif quotidien.
              Rétention brute : 90 jours · Version moteur {data.version}.
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
